import { InvestigationPredicate } from "../../lusca-search-api/generated-src";
import { compressAndSerializeData } from "../../helpers/compressionUtils";
import {
  isExclusion,
  predicateValues,
} from "../investigations/investigationUtils";

/** Minimal shape of the QuickSight embedding SDK filter groups we touch. */
export interface QsFilterColumn {
  DataSetIdentifier?: string;
  ColumnName?: string;
}
export interface QsCategoryFilter {
  FilterId?: string;
  Column?: QsFilterColumn;
  Configuration?: Record<string, unknown>;
}
export interface QsFilter {
  CategoryFilter?: QsCategoryFilter;
  [other: string]: unknown;
}
export interface QsFilterGroup {
  FilterGroupId?: string;
  Filters?: QsFilter[];
  [other: string]: unknown;
}

export const RULE_PARAM = "rule";

/** URL for the DLQ dashboard with an investigation rule to apply as filters once it loads. */
export function dashboardPathForRule(
  stage: string,
  rule: Array<InvestigationPredicate>
): string {
  const params = new URLSearchParams({ stage });
  params.set(RULE_PARAM, compressAndSerializeData(rule));
  return `/dashboard/dlq?${params.toString()}`;
}

const normalise = (name?: string) =>
  (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export interface RuleFilterPlan {
  /** Filter groups to send to updateFilterGroups (cloned, with the rule values applied). */
  updates: QsFilterGroup[];
  /** Rule fields for which the dashboard has no category filter to drive. */
  unmatchedFields: string[];
}

/**
 * Applies an investigation rule to the dashboard's existing filter groups.
 *
 * QuickSight filter groups are dashboard-specific (dataset identifiers, sheet scope, ids) and not
 * source-controlled, so instead of fabricating them we reuse the dashboard's own category filters:
 * for every predicate we look for a CategoryFilter whose column name matches the rule field and
 * replace its configuration with an exact-match list of the rule's value(s). Fields without a matching
 * dashboard filter are reported so the caller can tell the user.
 */
export function planRuleFilters(
  existing: QsFilterGroup[],
  rule: Array<InvestigationPredicate>
): RuleFilterPlan {
  const updates = new Map<string, QsFilterGroup>();
  const unmatchedFields: string[] = [];

  rule.forEach((predicate) => {
    // A QuickSight category list cannot say "none of": leave the dashboard filter unset.
    if (isExclusion(predicate)) return;
    const wanted = normalise(predicate.field);
    let applied = false;
    existing.forEach((group) => {
      const filters = group.Filters || [];
      const hit = filters.some(
        (f) => normalise(f.CategoryFilter?.Column?.ColumnName) === wanted
      );
      if (!hit) return;
      const key = group.FilterGroupId || JSON.stringify(group);
      const clone: QsFilterGroup =
        updates.get(key) ||
        (JSON.parse(JSON.stringify(group)) as QsFilterGroup);
      clone.Filters = (clone.Filters || []).map((f) => {
        if (
          !f.CategoryFilter ||
          normalise(f.CategoryFilter.Column?.ColumnName) !== wanted
        ) {
          return f;
        }
        applied = true;
        const existingConfiguration = f.CategoryFilter.Configuration || {};
        const filterListConfiguration: Record<string, unknown> = {
          ...((existingConfiguration.FilterListConfiguration as
            | Record<string, unknown>
            | undefined) || {}),
          MatchOperator: "CONTAINS",
          CategoryValues: predicateValues(predicate),
        };
        // QuickSight rejects explicit values combined with FILTER_ALL_VALUES. Preserve required
        // settings such as NullOption, but remove the mutually exclusive select-all marker.
        delete filterListConfiguration.SelectAllOptions;
        return {
          ...f,
          CategoryFilter: {
            ...f.CategoryFilter,
            Configuration: {
              ...existingConfiguration,
              FilterListConfiguration: filterListConfiguration,
            },
          },
        };
      });
      updates.set(key, clone);
    });
    if (!applied) unmatchedFields.push(predicate.field);
  });

  return { updates: Array.from(updates.values()), unmatchedFields };
}
